import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { RegistrationFlowType } from "../bidder-registration/constant";
import useTranslationHook from "../../../../hooks/useTranslationHook";
import { handleAuctionBidRegistration } from "../../../../../redux/action/buyerAction";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { createPortal } from "react-dom";
import { Trans } from "react-i18next";

const BidderRegistrationAgreementModal = ({
    show,
    onClose,
    proceedToPaymentStep,
}) => {
    const { state } = useLocation();
    const dispatch = useDispatch();
    const { t } = useTranslationHook();
    const user = useSelector((state) => state.auth.user);
    const lang = useSelector((state) => state.translation.lang);
    const registrationData = useSelector(
        (state) => state.buyer.buyerRegistration
    );
    const registration_id = useSelector(
        (state) => state.buyer.buyerRegistration.registration_id
    );

    const {
        propertyAuctionDate,
        propertyState,
        propertyCommunity,
        propertyCommunityAr,
        selectedSessionID,
    } = state ?? {};

    const isEnglish = lang === "en";

    const propAuctionDate =
        registrationData?.auction_start ?? propertyAuctionDate;

    const propState = registrationData?.state || propertyState;

    const propCommunity =
        (isEnglish
            ? registrationData?.community
            : registrationData?.community_ar) ||
        (isEnglish ? propertyCommunity : propertyCommunityAr);

    const registrationType = registrationData?.registrationType;
    const reservation_agreement_accepted =
        registrationData?.reservation_agreement_accepted;

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm({
        defaultValues: { reservation_agreement_accepted },
    });

    const { firstName, lastName } = useMemo(() => {
        if (!registrationData || !registrationType) {
            return { firstName: "", lastName: "" };
        }

        const stepOneData =
            registrationType === RegistrationFlowType.SELF
                ? registrationData.self?.stepOneData
                : registrationData.principal?.stepOneData;

        return {
            firstName: stepOneData?.firstName || "",
            lastName:
                stepOneData?.lastName !== "NA"
                    ? stepOneData?.lastName || ""
                    : "",
        };
    }, [registrationData, registrationType]);

    const signatureValue = `${firstName} ${lastName}`.trim();

    const submitRegistrationAgreement = async (formData) => {
        const { reservation_agreement_accepted, reservation_agreement_sign } =
            formData;

        const step = registrationType === RegistrationFlowType.SELF ? 2 : 3;

        const response = await dispatch(
            handleAuctionBidRegistration(
                selectedSessionID
                    ? {
                          registration_id: registration_id,
                          auction_session: selectedSessionID,
                          term_accepted: reservation_agreement_accepted ? 1 : 0,
                          signature: reservation_agreement_sign,
                          step: 4,
                      }
                    : {
                          step,
                          bid_registration: registration_id,
                          signature: reservation_agreement_sign,
                          term_accepted: reservation_agreement_accepted ? 1 : 0,
                      }
            )
        );

        const { code, error, msg } = response ?? {};

        if (code === 1 && error === 0) {
            toast.success(t(msg || "Registration successful"));
            proceedToPaymentStep?.();
        } else {
            toast.info(t(msg || "Something went wrong"));
            console.info("Bid Registration Error:", response);
        }
    };

    useEffect(() => {
        document.body.classList.toggle("modal-open", show);
        return () => {
            document.body.classList.remove("modal-open");
        };
    }, [show]);

    return createPortal(
        <div
            className={`modal fade place-bid-modal ${show ? "show d-block" : ""}`}
            tabIndex="-1"
            role="dialog"
        >
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5>{t("BidderRegistration")}</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            aria-label="Close"
                        />
                    </div>

                    <div className="modal-body pb0">
                        <ReservationAgreement
                            companyName={user?.first_name || "—"}
                            propertyAddress={
                                [t(propState), t(propCommunity)]
                                    .filter(Boolean)
                                    .join(", ") || "—"
                            }
                            auctionDate={
                                propAuctionDate
                                    ? moment(propAuctionDate).format(
                                          "MM-DD-YYYY"
                                      )
                                    : "—"
                            }
                            reservationDate={
                                propAuctionDate
                                    ? moment(propAuctionDate).format(
                                          "MM-DD-YYYY"
                                      )
                                    : "—"
                            }
                        />

                        <div className="check mb-3">
                            <input
                                id="agree"
                                type="checkbox"
                                className="css-checkbox"
                                {...register("reservation_agreement_accepted", {
                                    required: t(
                                        "You must agree to the terms before proceeding."
                                    ),
                                })}
                            />
                            <label htmlFor="agree" className="css-label">
                                {t(
                                    "I have read and agree to the terms of the reservation agreement."
                                )}
                            </label>

                            {errors.reservation_agreement_accepted && (
                                <p className="text-danger">
                                    {
                                        errors.reservation_agreement_accepted
                                            .message
                                    }
                                </p>
                            )}
                        </div>

                        <div className="mb-4">
                            <label className="form-label">
                                {t("Signature")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                defaultValue={signatureValue}
                                placeholder={t("Type your name")}
                                {...register("reservation_agreement_sign", {
                                    required: t("Signature is required"),
                                })}
                            />

                            {errors.reservation_agreement_sign && (
                                <p className="text-danger">
                                    {errors.reservation_agreement_sign.message}
                                </p>
                            )}
                        </div>

                        <div className="button-action">
                            <button
                                type="button"
                                className="btn btn-primary btn-full"
                                disabled={!isValid}
                                onClick={handleSubmit(
                                    submitRegistrationAgreement
                                )}
                            >
                                {t("SignAndSubmit")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.getElementById("root")
    );
};

export default BidderRegistrationAgreementModal;

const ReservationAgreement = ({
    companyName = "—",
    propertyAddress = "—",
    auctionDate = "—",
    reservationDate = "—",
}) => {
    return (
        <p className="lightgray-text">
            {/* This Reservation Agreement is between {companyName} and{" "}
            the Seller. By signing this agreement, the Seller
            confirms their intent to reserve the following property for auction:
            <br />

            Property Address: {propertyAddress}
            <br />
            Auction Date: {auctionDate}
            <br />

            Terms of Reservation:
            <br />
            1. The Seller agrees to provide all necessary documents related to the
            property.
            <br />
            2. The property will be reserved for auction on {reservationDate}.
            <br />
            3. The Seller acknowledges that the auction process will be conducted
            according to the terms outlined.
            <br />
            4. Any fees associated with the reservation will be communicated
            separately.
            <br />
            5. The Seller may withdraw the reservation prior to the auction date. */}

            <Trans
                i18nKey="reservationAgreement"
                values={{
                    companyName,
                    propertyAddress,
                    auctionDate,
                    reservationDate,
                }}
                components={{
                    1: <br />,
                    2: <br />,
                    3: <br />,
                    5: <br />,
                    6: <br />,
                    7: <br />,
                    8: <br />,
                    9: <br />,
                }}
            />
        </p>
    );
};
